/* Other Ops API client — the read endpoint plus thin wrappers over the
 * EXISTING ruled verbs the cards and rows dispatch to. No verb here was
 * invented for this surface:
 *
 *   retry / discard        POST /api/tasks/{id}/stuck-action   (WI-9)
 *   acknowledge / close    POST /api/tasks/{id}/close          (closure audit)
 *   resolve                POST /api/tasks/{id}/resolve        (generic)
 *
 * `retryWholeGroup` is the OFFICER verb (Ale 2026-08-09): the same
 * endpoint with NO `exclude_proposal_ids`. Choosing which leg is poison
 * is a PARTIAL retry and stays admin — `retryStuck` carries that.
 *
 * Flag detection: /api/ops/cards 404s while `rk_decks` is off (dark
 * ship), so a probe on it IS the flag read — same idiom as the RK deck
 * surface, no config round-trip. */

import { useEffect, useState } from 'react'
import { apiFetch, ApiError } from '../../lib/api'
import type { OpsAudience, OpsCardsResponse, OpsCounts, StuckActionResult } from './types'

/** Zeroed counts. No `unlocked` member (request_unlocked_copy is
 *  document state now) and no orphan / byte-duplicate member — the ops
 *  numbers can never be inflated by them. */
export const EMPTY_OPS_COUNTS: OpsCounts = {
  digest: 0,
  processed_unchanged: 0,
  officer_total: 0,
  stuck: 0,
  dead_letter: 0,
  unroutable: 0,
  system_failures: 0,
}

export async function fetchOpsCards(
  fundId?: string | null,
  audience: OpsAudience = 'officer'
): Promise<OpsCardsResponse> {
  const qs = new URLSearchParams({ audience })
  if (fundId) qs.set('fund_id', fundId)
  return apiFetch<OpsCardsResponse>(`/api/ops/cards?${qs.toString()}`)
}

/** THE OFFICER VERB. Retry the whole group — no leg selection, ever.
 *  `document_stuck` re-mints the dead-lettered event (async, `requeued`);
 *  `dead_letter_review` re-runs the write sequence synchronously and
 *  reports what actually happened. A re-run that dead-letters AGAIN comes
 *  back as `dead_lettered_again` with a reason and the task stays open —
 *  surface it, never a silent requeue. */
export async function retryWholeGroup(taskId: string): Promise<StuckActionResult> {
  return apiFetch<StuckActionResult>(`/api/tasks/${taskId}/stuck-action`, {
    method: 'POST',
    body: JSON.stringify({ action: 'retry' }),
  })
}

/** ADMIN: the partial retry. `excludeProposalIds` stamps the unticked
 *  legs rejected before the savepoint-atomic write re-runs (#806). */
export async function retryStuck(
  taskId: string,
  excludeProposalIds: string[] = [],
  note?: string
): Promise<StuckActionResult> {
  return apiFetch<StuckActionResult>(`/api/tasks/${taskId}/stuck-action`, {
    method: 'POST',
    body: JSON.stringify({
      action: 'retry',
      ...(excludeProposalIds.length ? { exclude_proposal_ids: excludeProposalIds } : {}),
      ...(note ? { note } : {}),
    }),
  })
}

/** ADMIN. The reason is mandatory server-side (422 without it); the
 *  dead-letter row stays for audit. */
export async function discardStuck(taskId: string, note: string): Promise<StuckActionResult> {
  return apiFetch<StuckActionResult>(`/api/tasks/${taskId}/stuck-action`, {
    method: 'POST',
    body: JSON.stringify({ action: 'discard', note }),
  })
}

/** daily_digest acknowledge (officer) and the unrouted-type close
 *  (admin). No record write rides on either. */
export async function closeTask(taskId: string) {
  return apiFetch(`/api/tasks/${taskId}/close`, { method: 'POST' })
}

/** ADMIN: the generic verb the never-dead-end fallback keeps clickable. */
export async function resolveTask(taskId: string, note?: string) {
  return apiFetch(`/api/tasks/${taskId}/resolve`, {
    method: 'POST',
    body: JSON.stringify(note?.trim() ? { note } : {}),
  })
}

export function opsErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    return `${err.statusText} (${err.status})`
  }
  return err instanceof Error ? err.message : 'request failed'
}

/** WI-9 honest outcomes. `spent` false means the work is STILL LIVE —
 *  a re-run that dead-lettered again leaves the task open server-side,
 *  so the row must stay actionable and say what happened. */
export function outcomeLine(result: StuckActionResult): {
  text: string
  bad: boolean
  spent: boolean
} {
  switch (result.outcome) {
    case 'requeued':
      return { text: 'retried — requeued; the worker replays it', bad: false, spent: true }
    case 'succeeded': {
      const written = result.written_count
      return {
        text: `retried — the write landed${written != null ? ` (${written} record row(s) written)` : ''}`,
        bad: false,
        spent: true,
      }
    }
    case 'partial':
      return { text: 'retried — landed with the excluded legs severed', bad: false, spent: true }
    case 'dead_lettered_again':
      return {
        text: `retried — dead-lettered again: ${result.failure_reason ?? 'no reason given'}`,
        bad: true,
        spent: false,
      }
    case 'resolution_question':
      return {
        text: `held for a resolution question — ${
          result.resolution_question ?? 'answer it in the review queue'
        }`,
        bad: false,
        spent: true,
      }
    case 'discarded':
      return { text: 'discarded — the row stays for audit', bad: false, spent: true }
    default:
      return { text: 'done', bad: false, spent: true }
  }
}

/* ── flag probe ──────────────────────────────────────────────────────── */

let probe: Promise<boolean> | null = null

/** True once GET /api/ops/cards answers 200; false on the dark 404. */
export function probeOpsSurface(): Promise<boolean> {
  if (!probe) {
    probe = fetchOpsCards()
      .then(() => true)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) return false
        // Auth/network hiccups must not latch the flag off for the session.
        probe = null
        return false
      })
  }
  return probe
}

/** Counts for the workbench's OTHER OPS line:
 *  `OTHER OPS <officer_total> · <digest> digest · <system_failures> system failures (admin)`.
 *  Both populations ride one officer read; they are never added together.
 *  The LINE never hides — the unroutable safety net lives on it. */
export function useOpsCounts(fundId?: string | null): OpsCounts {
  const [counts, setCounts] = useState<OpsCounts>(EMPTY_OPS_COUNTS)
  useEffect(() => {
    let cancelled = false
    fetchOpsCards(fundId)
      .then((payload) => {
        if (!cancelled) setCounts(payload.counts)
      })
      .catch(() => {
        if (!cancelled) setCounts(EMPTY_OPS_COUNTS)
      })
    return () => {
      cancelled = true
    }
  }, [fundId])
  return counts
}

/** null while probing, then the flag. */
export function useOpsSurfaceEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    probeOpsSurface().then((on) => {
      if (!cancelled) setEnabled(on)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return enabled
}
